import { INavLink } from "core-integration/src/hooks/types";
import { RootState } from "core-integration/src/store";
import { getEntry } from "core-integration/src/store/entries/reducers";
import {
  getCurrentEvent,
  getNextEvent,
} from "core-integration/src/store/events/reducers";
import { getServerError } from "core-integration/src/store/global/reducers";
import { getPlayerData } from "core-integration/src/store/player/reducers";
import { useCallback, useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";
import { useAuth } from "react-oidc-context";
import { useSelector } from "react-redux";
import { clearPingCookies } from "shared-utils/src/ping";
import { useTrackingContext } from "../../../contexts/TrackingContext";
import { navContainerStyles, navListStyles, navStyles } from "./Nav.css";
import NavItem from "./NavItem";

const NavEventDetails = {
  fixtures: "Fixtures",
  help: "Help",
  home: "Home",
  injuries: "Injuries",
  leagues: "Leagues",
  pickTeam: "Pick Team",
  podcast: "Podcast",
  points: "Points",
  priceChanges: "Price Changes",
  prizes: "Prizes",
  scout: "The Scout",
  signOut: "Sign Out",
  squadSelection: "Squad Selection",
  statistics: "Statistics",
  status: "Status",
  transfers: "Transfers",
} as const;

type NavEventDetail = (typeof NavEventDetails)[keyof typeof NavEventDetails];

type NavLink = INavLink & {
  eventDetail: NavEventDetail;
};

const Nav = () => {
  const { t } = useTranslation();
  const player = useSelector(getPlayerData);
  const currentEvent = useSelector(getCurrentEvent);
  const nextEvent = useSelector(getNextEvent);
  const serverError = useSelector(getServerError);
  const entry = useSelector((state: RootState) =>
    player?.entry ? getEntry(state, player.entry) : null,
  );

  const auth = useAuth();
  const { fireClickTrackEvent } = useTrackingContext();
  const navRef = useRef<HTMLElement>(null);

  const trackNavigationClick = (eventDetail: NavEventDetail) => {
    fireClickTrackEvent({
      event_component: "navigation click",
      event_category: "fantasy top navigation",
      event_detail: eventDetail,
    });
  };

  const handleLogout = useCallback(
    (e: Event) => {
      e.preventDefault();
      window.branch?.logout();
      clearPingCookies();
      auth
        .signoutRedirect({
          id_token_hint: auth.user?.id_token,
          post_logout_redirect_uri: `${window.location.origin}/`,
        })
        .catch((error) => {
          console.error("Logout failed:", error);
        });
    },
    [auth],
  );

  // Setup a click handler that intercepts clicks on the logout link
  useEffect(() => {
    const handleNavClick = (e: MouseEvent) => {
      const target = e.target as HTMLElement;
      const linkElement = target.closest("a");

      if (!linkElement) return;

      // Check if it's the logout link
      const isLogoutLink =
        linkElement.textContent?.includes(t("navigation.logout", "Sign Out")) ||
        linkElement
          .querySelector("span")
          ?.textContent?.includes(t("navigation.logout", "Sign Out"));

      if (isLogoutLink) {
        handleLogout(e);
      }
    };

    const navElement = navRef.current;
    if (navElement) {
      navElement.addEventListener("click", handleNavClick);
    }

    return () => {
      if (navElement) {
        navElement.removeEventListener("click", handleNavClick);
      }
    };
  }, [handleLogout]);

  const gameLiveForEntry =
    currentEvent && entry && entry.started_event <= currentEvent.id;

  const logoutNavLink: NavLink = {
    useRouter: false,
    href: "#", // Changed to a simple hash link that won't navigate away
    text: t("navigation.logout", "Sign Out"),
    eventDetail: NavEventDetails.signOut,
  };

  const podcastNavLink: NavLink = {
    useRouter: false,
    href: "//premierleague.com/official-fpl-podcast/",
    text: t("navigation.podcast", "Podcast"),
    eventDetail: NavEventDetails.podcast,
  };

  const injuriesNavLink: NavLink = {
    useRouter: false,
    href: "//premierleague.com/latest-player-injuries/",
    text: t("navigation.injuries", "Injuries"),
    eventDetail: NavEventDetails.injuries,
  };

  let links: NavLink[] = [];
  const useRouter = !serverError;

  if (player?.entry) {
    if (nextEvent) {
      links.push({
        useRouter,
        href: "/my-team",
        text: t("navigation.pickTeam", "Pick Team"),
        eventDetail: NavEventDetails.pickTeam,
      });
    }
    if (gameLiveForEntry) {
      links.push({
        useRouter,
        href: `/entry/${player.entry}/event/${currentEvent.id}`,
        text: t("navigation.points", "Points"),
        eventDetail: NavEventDetails.points,
      });
    }
    links.push(
      {
        useRouter,
        href: "/transfers",
        text: t("navigation.transfers", "Transfers"),
        eventDetail: NavEventDetails.transfers,
      },
      {
        useRouter,
        href: "/leagues",
        text: t("navigation.leagues", "Leagues"),
        eventDetail: NavEventDetails.leagues,
      },
      {
        useRouter,
        href: "/help",
        text: t("navigation.help", "Help"),
        eventDetail: NavEventDetails.help,
      },
      {
        useRouter,
        href: "/the-scout",
        text: t("navigation.scout", "The Scout"),
        eventDetail: NavEventDetails.scout,
      },
    );
    if (currentEvent) {
      links.push(
        {
          useRouter,
          href: `/price-changes`,
          text: t("navigation.priceChanges", "Price Changes"),
          eventDetail: NavEventDetails.priceChanges,
        },
        {
          useRouter,
          href: "/status",
          text: t("navigation.status", "Status"),
          eventDetail: NavEventDetails.status,
        },
      );
    }
    links.push(
      {
        useRouter,
        href: "/fixtures",
        text: t("navigation.fixtures", "Fixtures"),
        eventDetail: NavEventDetails.fixtures,
      },
      injuriesNavLink,
      {
        useRouter,
        href: "/statistics",
        text: t("navigation.stats", "Statistics"),
        eventDetail: NavEventDetails.statistics,
      },
      podcastNavLink,
      {
        useRouter,
        href: "/prizes",
        text: t("navigation.prizes", "Prizes"),
        eventDetail: NavEventDetails.prizes,
      },
      logoutNavLink,
    );
  } else if (player) {
    if (nextEvent) {
      links.push({
        useRouter,
        href: "/squad-selection",
        text: t("navigation.squadSelection", "Squad Selection"),
        eventDetail: NavEventDetails.squadSelection,
      });
    }
    links.push(
      {
        useRouter,
        href: "/help",
        text: t("navigation.help", "Help"),
        eventDetail: NavEventDetails.help,
      },
      logoutNavLink,
    );
  } else {
    links = [
      {
        useRouter,
        href: "/",
        text: t("navigation.home", "Home"),
        eventDetail: NavEventDetails.home,
      },
      {
        useRouter,
        href: "/prizes",
        text: t("navigation.prizes", "Prizes"),
        eventDetail: NavEventDetails.prizes,
      },
      {
        useRouter,
        href: "/the-scout",
        text: t("navigation.scout", "The Scout"),
        eventDetail: NavEventDetails.scout,
      },
      {
        useRouter,
        href: `/price-changes`,
        text: t("navigation.priceChanges", "Price Changes"),
        eventDetail: NavEventDetails.priceChanges,
      },
      injuriesNavLink,
      podcastNavLink,
      {
        useRouter,
        href: "/help",
        text: t("navigation.help", "Help"),
        eventDetail: NavEventDetails.help,
      },
      {
        useRouter,
        href: "/statistics",
        text: t("navigation.stats", "Statistics"),
        eventDetail: NavEventDetails.statistics,
      },
    ];
  }

  return (
    <nav ref={navRef} className={navStyles}>
      <div className={navContainerStyles}>
        <ul className={navListStyles}>
          {links.map((link) => (
            <NavItem
              {...link}
              key={link.eventDetail}
              onClick={() => trackNavigationClick(link.eventDetail)}
            />
          ))}
        </ul>
      </div>
    </nav>
  );
};

export default Nav;
